import { Button } from "@mui/material"
import { useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { IfTooltip } from "lib/if-tooltip"
import { useReviewApprovalState } from "./use-review-approval-state"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { useScheduleEditorSeed } from "../../hooks/use-schedule-editor-seed"
import { confirm } from "lib/@dialogs/confirm"
import { useParams } from "react-router"
import { approveDraftReview } from "../../controller/approve-draft-review"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigateProject } from "authoring/routes/project/utilities/project-navigation"

export function ApproveReviewButton() {
    const { target, goToNext } = useBoundContext()
    const { seedSource } = useScheduleEditorSeed()
    const { projectId, scheduleId } = useParams()
    const { status } = target
    const [submitting, setSubmitting] = useState(false)

    const { allApproved, trackedChangeCount, amendIdsToCheck } = useReviewApprovalState()
    const hasAmendIdsForAllChanges = trackedChangeCount > 0 && amendIdsToCheck.length === trackedChangeCount
    const hasAmendIdsToCheck = amendIdsToCheck.length > 0
    const isNewSchedule = seedSource === SeedSource.NEW
    const disabled =
        !allApproved ||
        (isNewSchedule ? !hasAmendIdsToCheck : !hasAmendIdsForAllChanges) ||
        status === DraftStatus.APPROVED ||
        submitting

    let disabledMessage = ""
    if (trackedChangeCount === 0) {
        disabledMessage = "No review changes are available yet"
    } else if (!allApproved) {
        disabledMessage = "All individual changes need to be approved before approving the review"
    } else if (isNewSchedule && !hasAmendIdsToCheck) {
        disabledMessage = "Review changes are not ready to approve yet"
    } else if (status === DraftStatus.APPROVED) {
        disabledMessage = "Review was already approved"
    } else if (submitting) {
        disabledMessage = "Approval is already in progress"
    }

    return (
        <IfTooltip condition={disabled} title={disabledMessage}>
            <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<Iconify icon="akar-icons:double-check" />}
                disabled={disabled}
                onClick={handleApproveReviewClick}
                data-cy="approve-review-button"
            >
                Approve
            </Button>
        </IfTooltip>
    )

    function handleApproveReviewClick() {
        if (submitting) return
        handleApproveReview({
            projectId,
            scheduleId,
            amendIdsToCheck,
            goToNext,
            setSubmitting,
        })
    }
}

async function handleApproveReview({ projectId, scheduleId, amendIdsToCheck, goToNext, setSubmitting }) {
    const title = "Approve Review"
    const message = "Are you sure you want to approve this draft review?"
    try {
        const confirmApprove = await confirm(message, title)
        if (confirmApprove) {
            setSubmitting(true)
            const approved = await approveDraftReview({ projectId, scheduleId, amendIds: amendIdsToCheck })
            if (approved) {
                successSnackbar("Review approved successfully")
                if (goToNext) {
                    await goToNext("approved")
                } else {
                    navigateProject(projectId)
                }
            } else {
                errorSnackbar("Failed to approve review")
            }
        }
    } catch (error) {
        errorSnackbar(error?.message ?? "Failed to approve review")
    } finally {
        setSubmitting(false)
    }
}
